import { Avatar, Space } from 'antd';
import { UserOutlined } from '@ant-design/icons';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useGetWorkersQuery } from 'app/services/workers';
import { useGetUnitQuery } from 'app/services/me';
import { Worker } from 'app/types/worker';
import { DataTable, Column, ColumnTypes } from 'components/Datatable/DataTable';
import { useScreenBreakpoint } from 'hooks';

const expiredColor = '#f5222d';

interface TableProps {
  showActive: boolean;
}

const WorkerAvatar = ({ worker }: { worker: Worker }) => (
  <Avatar size={32} src={worker.PhotoUrl} icon={<UserOutlined />} />
);

const Table = ({ showActive }: TableProps) => {
  const { t } = useTranslation();
  const { isMobile } = useScreenBreakpoint();

  const { data: unit, isLoading: isUnitLoading, isFetching: isUnitFetching } = useGetUnitQuery({});

  const unitId = unit?.Id;

  const { data: workers = [], isLoading, isFetching } = useGetWorkersQuery(
    { unitId: unitId || 0, isActive: showActive },
    { skip: !unitId }
  );

  const link = (worker: Worker) => `/workers/${worker.Id}`;

  const firstNameColumn: Column = {
    key: 'FirstName',
    title: t('entities.worker.firstname'),
    type: ColumnTypes.LINK,
    hasSearch: true,
    hasSort: true,
    link,
  };

  // The phone table shows only the first and the last column, so the first one carries photo and name together.
  const firstColumns: Column[] = isMobile
    ? [ {
      key: 'LastName',
      title: t('entities.worker.fullname'),
      type: ColumnTypes.CUSTOM,
      hasSearch: true,
      hasSort: true,
      defaultOrder: 'ascend',
      renderAs: (worker: Worker) => (
        <Space>
          <WorkerAvatar worker={worker} />
          <Link to={link(worker)}>{worker.LastName} {worker.FirstName}</Link>
        </Space>
      ),
    }, firstNameColumn ]
    : [
      {
        key: 'Id',
        title: t('entities.worker.photourl'),
        type: ColumnTypes.CUSTOM,
        width: 72,
        renderAs: (worker: Worker) => <WorkerAvatar worker={worker} />,
      },
      {
        key: 'LastName',
        title: t('entities.worker.lastname'),
        type: ColumnTypes.LINK,
        hasSearch: true,
        hasSort: true,
        defaultOrder: 'ascend',
        link,
      },
      firstNameColumn,
    ];

  const columns: Column[] = [
    ...firstColumns,
    {
      key: 'JobTitle',
      title: t('entities.worker.jobtitle'),
      type: ColumnTypes.TEXT,
      hasSearch: true,
      hasSort: true,
    },
    {
      key: 'EmployerName',
      title: t('entities.worker.employername'),
      type: ColumnTypes.TEXT,
      hasSearch: true,
      hasSort: true,
    },
    {
      key: 'TaxNumber',
      title: t('entities.worker.taxnumber'),
      type: ColumnTypes.TEXT,
      hasSearch: true,
      hasSort: true,
    },
    {
      key: 'DateOfBirth',
      title: t('entities.worker.dateofbirth'),
      type: ColumnTypes.DATE,
      hasSort: true,
      hasDateFilter: true,
    },
  ];

  return (
    <DataTable
      data={workers}
      columns={columns}
      isLoading={isLoading || isUnitLoading || isFetching || isUnitFetching}
      pagination={true}
      defaultSort="LastName"
      tableKey="workers"
      rowStatusColor={(worker: Worker) => (worker.HasExpiredDocuments ? expiredColor : undefined)}
    />
  );
};

export default Table;
